/**
 * src/types/charts/line.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Multi-series line chart — types, spacing, animation styles, and defaults.
 * Sprint D1 rewrite: every data point has a required seriesName.
 */

import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_LAYOUT, DEFAULT_AXES, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type LineAnimationStyle =
  | 'classic' | 'drawLine' | 'sequential' | 'trace' | 'growFromAxis'
  | 'reveal' | 'segmentBySegment' | 'pulse' | 'cascade' | 'fadeScale'
  | 'staggeredReveal' | 'writeOn' | 'riseUp';

export const LINE_ANIMATION_STYLES: LineAnimationStyle[] = [
  'classic', 'drawLine', 'sequential', 'trace', 'growFromAxis',
  'reveal', 'segmentBySegment', 'pulse', 'cascade', 'fadeScale',
  'staggeredReveal', 'writeOn', 'riseUp',
];

/** 'drawLine' is a whole-line style — not applicable as a per-point override. */
export const LINE_ELEMENT_PANEL_STYLES: LineAnimationStyle[] =
  LINE_ANIMATION_STYLES.filter(s => s !== 'drawLine');


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Multi-series line chart data point.
 * Points are connected in array-index order within each series.
 * Per-point style lives in styleOverride (DataPointStyle).
 */
export interface LineDataPoint extends ChartDataPoint {
  chartType:   'line';
  value:       number;       // Y-axis value
  seriesName:  string;       // REQUIRED — which series this point belongs to
}


// ─────────────────────────────────────────────────────────────────────────────
// SERIES STYLE
// ─────────────────────────────────────────────────────────────────────────────

export interface LineSeriesStyle {
  color?:         string;                                    // canonical series color
  lineStyle?:     'solid' | 'dashed' | 'dotted' | 'dash-dot';
  fillOpacity?:   number;                                    // overrides spacing.fillOpacity for this series
  lineThickness?: number;                                    // overrides spacing.lineThickness for this series
  showPoints?:    boolean;
  pointSize?:     number;
  endLabel?:        'none' | 'right' | 'left';
  dataLabelFormat?: 'none' | 'value' | 'name' | 'name+value';
  labelFontSize?:   number;
  labelColor?:      string;
  labelBgStyle?:    'none' | 'pill' | 'underline';
  labelBgColor?:    string;
  showDot?:         boolean;  // colored dot before label text
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface LineSpacingConfig {
  chartType:           'line';
  pointSize:           number;   // marker radius px. Range: 0–60. Default: 10.
  lineThickness:       number;   // stroke width px. Range: 1–20. Default: 5.
  pointSpacingFactor:  number;   // horizontal spread %. Range: 20–400. Default: 100.
  fillOpacity:         number;   // global area fill opacity. 0 = no fill. Range: 0–1. Default: 0.
  gradientFill:        boolean;  // gradient from opaque at line to transparent at baseline. Default: false.
  negativeFillColor?:  string;   // fill color for area below zero (optional).
  seriesOverrides:     Record<string, LineSeriesStyle>;  // REQUIRED — per-series style overrides.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const LINE_DEFAULTS: ChartConfigBase<LineSpacingConfig> = {
  chartType: 'line',
  content: {
    titles:    [makeTitle('Revenue vs Expenses')],
    subtitles: [makeSubtitle('Jan – Jun 2024')],
    infoBoxes: [],
  },
  animation:  { duration: 5, style: 'classic', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout:     { ...DEFAULT_LAYOUT },
  axes: {
    x: { ...DEFAULT_AXES.x, tickLength: 0 },
    y: { ...DEFAULT_AXES.y },
  },
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS },
  spacing: {
    chartType:          'line',
    pointSize:          10,
    lineThickness:      5,
    pointSpacingFactor: 100,
    fillOpacity:        0,
    gradientFill:       false,
    seriesOverrides: {
      'Revenue':  { color: '#f26419', lineStyle: 'solid' },   // brand orange
      'Expenses': { color: '#0fb8a0', lineStyle: 'dashed' },  // brand teal
    },
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};

// ─────────────────────────────────────────────────────────────────────────────
// ZOD SPACING SCHEMA
// ─────────────────────────────────────────────────────────────────────────────

export const lineSpacingZodSchema = z.object({
  chartType:          z.literal('line'),
  pointSize:          z.number().min(0).max(60),
  lineThickness:      z.number().min(1).max(20),
  pointSpacingFactor: z.number().min(20).max(400),
  fillOpacity:        z.number().min(0).max(1),
  gradientFill:       z.boolean(),
  negativeFillColor:  z.string().optional(),
  seriesOverrides:    z.record(z.string(), z.object({
    color:         z.string().optional(),
    lineStyle:     z.enum(['solid', 'dashed', 'dotted', 'dash-dot']).optional(),
    fillOpacity:   z.number().min(0).max(1).optional(),
    lineThickness: z.number().min(1).max(20).optional(),
    showPoints:    z.boolean().optional(),
    pointSize:     z.number().min(0).max(60).optional(),
    endLabel:        z.enum(['none', 'right', 'left']).optional(),
    dataLabelFormat: z.enum(['none', 'value', 'name', 'name+value']).optional(),
    labelFontSize:   z.number().min(8).max(36).optional(),
    labelColor:      z.string().optional(),
    labelBgStyle:    z.enum(['none', 'pill', 'underline']).optional(),
    labelBgColor:    z.string().optional(),
    showDot:         z.boolean().optional(),
  })),
});


// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES — consumed by chart.schema.ts merger and getChartSchemaSlice()
// ─────────────────────────────────────────────────────────────────────────────

export const LINE_SCHEMA_NOTES: Record<string, SchemaNote> = {

  'line.pointSize': {
    description: 'Radius of data point markers in canvas pixels.',
    type:    'number',
    range:   '0–60',
    default: 10,
    unit:    'canvas pixels (radius)',
    affects: ['data point marker size'],
    bounds:  ['0 = markers hidden (line only).'],
    synergizes: ['line.lineThickness — thick line with tiny dots looks unbalanced'],
    tensions:   ['data count — large points on dense data causes visual crowding'],
    agentNote:  "0 = hidden markers (clean line only). Values 6–14 are standard. Values above 20 are decorative; work for ≤6 points but overwhelm at 10+.",
  },

  'line.lineThickness': {
    description: 'Stroke width of the connecting line in canvas pixels.',
    type:    'number',
    range:   '1–20',
    default: 5,
    unit:    'canvas pixels (stroke width)',
    affects: ['connecting line visual weight'],
    bounds:  ['Min 1. Max 20.'],
    synergizes: ['line.pointSize — scale together for visual balance'],
    tensions:   [],
    agentNote:  "2–6 = 'normal'. 7–12 = 'bold / social media'. >14 looks more like a bar chart fill than a line.",
  },

  'line.pointSpacingFactor': {
    description: 'Controls horizontal spread of data points as a percentage of available chart width. 100 = full width spread. 50 = compressed. 200 = expanded (may overflow).',
    type:    'number',
    range:   '20–400',
    default: 100,
    unit:    'percentage of chart width',
    affects: ['horizontal distribution of all data points'],
    bounds:  ['Values above 100 may cause last point to overflow chart margins.'],
    synergizes: [],
    tensions:   ['layout.chartMargin.right — pushing above 120 clips points past margins at default settings'],
    agentNote:  "Almost always left at 100. Only change for 2–3 points (spread further apart) or 15+ points (compressed together).",
  },

  'line.fillOpacity': {
    description: 'Global default opacity of the area fill beneath each series line. Per-series override via seriesOverrides[name].fillOpacity takes precedence. 0 = no fill (classic line chart).',
    type:    'number',
    range:   '0–1',
    default: 0,
    unit:    'float 0–1',
    affects: ['area fill visibility beneath each line'],
    bounds:  ['Range: 0–1.'],
    synergizes: ['line.gradientFill — gradient mode makes higher opacity values more readable', 'seriesOverrides.fillOpacity — per-series override'],
    tensions:   ['background.color — opaque fill on colored background reduces data label contrast'],
    agentNote:  "0 = classic line chart (no fill). 0.15–0.25 = subtle area hint. 0.35 = standard area chart. Per-series fillOpacity wins over this global value.",
  },

  'line.gradientFill': {
    description: 'When true, the area fill fades from opaque at the line to transparent at the baseline (vertical gradient). When false, flat fill at fillOpacity. Only visible when fillOpacity > 0.',
    type:    'boolean',
    range:   'true | false',
    default: false,
    unit:    'boolean',
    affects: ['area fill style — gradient vs flat'],
    bounds:  [],
    synergizes: ['line.fillOpacity — gradient only visible when fillOpacity > 0'],
    tensions:   [],
    agentNote:  "Only meaningful when fillOpacity > 0. true (gradient) emphasizes the line while showing magnitude. false (flat) creates bolder fills.",
  },

  'line.seriesOverrides': {
    description: 'Per-series style overrides keyed by seriesName. Always present (may be empty {}). Each key must match a seriesName value present in the data array.',
    type:    'Record<string, LineSeriesStyle>',
    range:   'N/A',
    default: '{}',
    unit:    'N/A',
    affects: ['per-series color, line style, fill opacity, line thickness, point display, end labels'],
    bounds:  [],
    synergizes: ['spacing.fillOpacity — global fallback when per-series fillOpacity not set'],
    tensions:   [],
    agentNote:  "color here is canonical — keep in sync with d.color on all matching data points. lineStyle:'dashed' for forecasts/projections. fillOpacity:0.15-0.25 = subtle area fill. endLabel:'right' renders the series name at the right edge of the line. Max 6 series before colours clash.",
  },

  'dataPoint.line.seriesName': {
    description: 'REQUIRED. Series this data point belongs to. All points with the same seriesName are drawn as one connected line.',
    type:    'string',
    range:   'any non-empty string',
    default: null,
    unit:    'N/A',
    affects: ['series grouping', 'line path connection', 'legend entries'],
    bounds:  ['Must not be empty.'],
    synergizes: ['seriesOverrides — per-series styling keyed by this value'],
    tensions:   [],
    agentNote:  "Every data point must have seriesName set. For a single metric use one series name consistently across all points. Max 6 series before colours clash — beyond that use small-multiples instead.",
  },

  'dataPoint.line.value': {
    description: 'Numeric Y-axis value for a single line data point. X positions are aligned by category name (d.name) across all series.',
    type:    'number',
    range:   'any number including negative',
    default: null,
    unit:    'arbitrary numeric unit',
    affects: ['point y-position', 'connecting line path', 'y-axis tick range'],
    bounds:  [],
    synergizes: ['line.pointSpacingFactor'],
    tensions:   ['point order — within each series, points are connected in array order'],
    agentNote:  "CRITICAL: within each series, points are connected in array order. Time-series data MUST be ordered left-to-right. X positions are aligned by d.name across series — same name = same X position.",
  },

  'line.seriesOverrides.dataLabelFormat': {
    description: "Per-series data label format. Controls what text appears above each data point in this series.",
    type: "'none' | 'value' | 'name' | 'name+value'",
    range: "'none' | 'value' | 'name' | 'name+value'",
    default: null,
    unit: 'enum',
    affects: ['data label text content per series'],
    bounds: [],
    synergizes: ['dataLabels.visible — global fallback when per-series format not set'],
    tensions: [],
    agentNote: "'none' hides labels. 'value' shows numeric value. 'name' shows category name. 'name+value' shows both. Falls back to global dataLabels.visible when not set.",
  },

  'line.seriesOverrides.labelBgStyle': {
    description: "Background treatment for data labels in this series. 'pill' = rounded dark background. 'underline' = thin line below text.",
    type: "'none' | 'pill' | 'underline'",
    range: "'none' | 'pill' | 'underline'",
    default: '"none"',
    unit: 'enum',
    affects: ['data label background style'],
    bounds: [],
    synergizes: ['labelBgColor — color of the pill/underline'],
    tensions: [],
    agentNote: "'pill' creates a dark rounded rect behind label text for readability on busy charts. 'underline' is subtle. Default 'none' = text only.",
  },

  'line.seriesOverrides.showDot': {
    description: "When true, draws a small colored dot (series color) before each data label. Like an inline legend indicator.",
    type: 'boolean',
    range: 'true | false',
    default: false,
    unit: 'boolean',
    affects: ['colored dot indicator before data label text'],
    bounds: [],
    synergizes: ['dataLabelFormat — dot only visible when labels are shown'],
    tensions: [],
    agentNote: "Useful for distinguishing which series a label belongs to on multi-series charts. Default false = no dot.",
  },

};
